/**
 * Standard layout for “prompt + attached library context” sent to the model:
 * CONTEXT block with one XML-like wrapper per item, tag derived from the prompt’s folder/category
 * for that context; then PROMPT with the user instructions.
 */

export const EXECUTION_CONTEXT_HEADER = 'CONTEXT';
export const EXECUTION_PROMPT_HEADER = 'PROMPT';

/**
 * Map a human folder label (e.g. "Brand guidelines", "Examples") to a safe tag name.
 * @param {string} folderLabel
 * @returns {string}
 */
export function folderLabelToXmlTag(folderLabel) {
  const raw = String(folderLabel || '').trim() || 'General';
  const s = raw
    .replace(/&/g, 'and')
    .replace(/[^a-zA-Z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');
  let tag = s || 'General';
  if (/^[0-9]/.test(tag)) {
    tag = `ctx_${tag}`;
  }
  return tag;
}

/**
 * @param {Array<{ id?: string, name?: string, content?: string, category?: string }>} contexts
 * @param {Record<string, string>} [contextCategoryById] Prompt-specific folder: context id → category label
 * @returns {string} CONTEXT section only, or '' if nothing to include
 */
export function formatContextSectionForExecution(contexts, contextCategoryById = {}) {
  if (!Array.isArray(contexts) || contexts.length === 0) return '';

  const blocks = [];
  for (const ctx of contexts) {
    if (!ctx?.content) continue;
    const id = ctx.id;
    const folderRaw =
      (id != null && contextCategoryById[id] != null && String(contextCategoryById[id]).trim() !== ''
        ? contextCategoryById[id]
        : ctx.category) || 'General';
    const folder = String(folderRaw).trim() || 'General';
    const tag = folderLabelToXmlTag(folder);
    const innerName = String(ctx.name || '').trim();
    let inner = innerName ? `[${innerName}]\n` : '';
    inner += String(ctx.content).trimEnd();
    blocks.push(`<${tag}>\n${inner}\n</${tag}>`);
  }

  if (blocks.length === 0) return '';
  return `${EXECUTION_CONTEXT_HEADER}\n\n${blocks.join('\n\n')}`;
}

/**
 * @param {{ contexts: Array, contextCategoryById?: Record<string,string>, promptPart: string }} opts
 * @returns {string} Full message for execution APIs
 */
export function buildFullExecutionPayload({ contexts, contextCategoryById = {}, promptPart }) {
  const ctxSection = formatContextSectionForExecution(contexts, contextCategoryById);
  const promptTrim = String(promptPart || '').trim();
  if (!ctxSection) return promptTrim;
  if (!promptTrim) return ctxSection;
  return `${ctxSection}\n\n${EXECUTION_PROMPT_HEADER}\n\n${promptTrim}`;
}
